/**
 * **The attack forecast** (`projects/target-hover-forecast.md` 1.2): what one weapon or tech attack
 * would roll against one target, read before anything commits. The browser's targeting-mode hover
 * panel and the CLI's `forecast-attack` both read it, so the two cannot disagree.
 *
 * **It is a dry run of the resolution, not a re-derivation** (`event-engine-to-hit` 5.1). It calls
 * the stage functions `resolveSingleTarget` calls, in the same order (S1 {@link attackOverlay}, S2
 * {@link deriveAttackFacts}, S2.5 {@link foldAttackCover}, S3 {@link foldAttackModifiers}), then
 * proposes the attack roll through its replacements as `drawToHit` does ({@link proposedAttackRoll})
 * and reads the net accuracy and the advantage off that roll. Nothing is drawn. The declared phase
 * is dry-run too ({@link dryRunAttackDeclared}, the probe `previewAttackDeclaredChoice` reads): the
 * forecast reads the charge costs it raises. Damage reads the helpers the damage step reads
 * ({@link instanceDamageType}, {@link effectiveTargetArmor}, {@link mitigate}, `foldedResistance`,
 * `immuneToDamage`). `attackForecast.parity.test.ts` pins it against the recorded `to_hit` and
 * `damage` steps of a real non-interactive resolution; a new stage added to S1-S3 must be added here too.
 *
 * **Every prompt is declined (F-TH1).** A declared-phase pick (ammo) counts only when `choices`
 * preselects it, and the view's `dependsOn` names it otherwise. A reaction to the declaration
 * (Interdiction Point), to the outcome (Hunker Down, Barrel Roll, Valiant Aid) or to the damage
 * (Brace, Intercede) is a live decision and forecasts as declined, which is what the
 * non-interactive resolve does.
 *
 * **Area attacks forecast per caught target** ({@link forecastAreaAttack}) from the cell they are
 * aimed at, with the catch's size and the Blast's measuring center. Without an aimed cell an area
 * weapon is illegal here, so the browser's hover, which names a unit, stays single-target.
 *
 * Pure: no recorded dice, no mutation; the dry run's ledger is discarded.
 */

import {
  isAreaRange,
  isCharacter,
  weaponInstanceKey,
  type AttackForecast,
  type AttackForecastContribution,
  type AttackForecastCost,
  type AttackForecastDamage,
  type AttackForecastDamageType,
  type AttackForecastResult,
  type Cell,
  type Combatant,
  type DamageType,
  type EncounterState,
  type WeaponProfile,
} from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { foldedResistance, immuneToDamage, recordedChoices, type LoadedOverlay } from "./effects";
import { waiverOnCrit } from "./events/gameEvents";
import { areaCatchOf } from "./resolve/attack";
import { dryRunAttackDeclared } from "./resolve/preview";
import { overshieldValue } from "./overshield";
import { foldAttackCover, foldAttackModifiers } from "./resolve/attackModifiers";
import { deriveAttackFacts } from "./resolve/attackFacts";
import { resolveVariableDamageType } from "./resolve/attackPreRoll";
import { attackOverlay } from "./resolve/attackOverlay";
import { archaicCantHarm, effectiveTargetArmor, isPhysical } from "./resolve/damage";
import { mitigateDamageInstance } from "./resolve/damagePacket";
import { instanceDamageType } from "./resolve/damageApply";
import { isNeedsDecision } from "./resolve/types";
import { settleConsumeChoice } from "./resolve/weaponConsume";
import { defenseFor, proposedAttackRoll, toHitBonus } from "./resolve/toHit";
import { VARIABLE_DAMAGE_TYPE_OPTIONS, type EffectsConfig } from "./resolve/types";
import { isMeleeActionAttack, isNoDamageMeleeAction, namesDestroyedWeapon, namesUnknownWeapon, selectWeapon } from "./resolve/weapons";

/** Named arguments for {@link forecastAttack}: the fields of the `request_attack` it forecasts, plus the overlay. */
export type ForecastAttackArgs = {
  attackerId: string;
  targetId: string;
  /** The weapon instance key (as `request_attack.weapon`), or an action id (`act_invade`, `act_ram`). */
  weapon?: string;
  profile?: string;
  /** The attack's declared `--accuracy` (default 0). */
  accuracy?: number;
  tech?: boolean;
  /** Preselected answers, keyed as `request_attack.choices` (a variable damage type, an ammo pick). */
  choices?: Record<string, string>;
  /** The merged entity overlay the room resolves with. Absent → the seed overlay, as a resolve with `effects: {}` gets. */
  overlay?: LoadedOverlay;
  /** An area weapon's aimed cell (as `request_attack.target`'s cell); absent, it is aimed at the target. */
  cell?: Cell;
};

/**
 * Forecast one attack. Returns `{ illegal }` rather than throwing for an attack the room would
 * reject or the forecast cannot describe: an unknown combatant or weapon, a tech attack on an
 * object (`decisions/0057`), or an area weapon.
 */
export function forecastAttack(state: EncounterState, args: ForecastAttackArgs): AttackForecastResult {
  const attacker = state.combatants[args.attackerId];
  if (attacker === undefined) return { illegal: `no combatant "${args.attackerId}"` };
  const target = state.combatants[args.targetId];
  if (target === undefined) return { illegal: `no combatant "${args.targetId}"` };
  const intent: RequestAttack = {
    type: "request_attack",
    attacker: attacker.id,
    target: { kind: "unit", id: target.id },
    accuracy: args.accuracy ?? 0,
    ...(args.weapon !== undefined ? { weapon: args.weapon } : {}),
    ...(args.profile !== undefined ? { profile: args.profile } : {}),
    ...(args.tech ? { tech: true } : {}),
    ...(args.choices !== undefined ? { choices: args.choices } : {}),
  };
  // `resolveSingleTarget`'s own kind-reject, as a value instead of a throw.
  if (intent.tech && !isCharacter(target)) {
    return { illegal: `tech attacks can't target "${target.name}": it is not a character (objects have no E-DEFENSE)` };
  }
  if (!intent.tech) {
    // A named weapon the attacker does not carry or has lost to System Trauma (`selectWeapon` would
    // throw), or one it swaps away from (a weaponless attacker's strike), is illegal here.
    const unknown = `${attacker.name} has no weapon "${intent.weapon}"`;
    if (namesUnknownWeapon(attacker, intent.weapon)) return { illegal: unknown };
    if (namesDestroyedWeapon(attacker, intent.weapon)) return { illegal: `${attacker.name}'s weapon "${intent.weapon}" is destroyed` };
    const selected = selectWeapon(attacker, intent.weapon, undefined);
    if (intent.weapon !== undefined && !isMeleeActionAttack(intent.weapon) && weaponInstanceKey(selected) !== intent.weapon) {
      return { illegal: unknown };
    }
    // An area weapon is forecast from the cell it is aimed at (`forecastAreaAttack`); the browser's
    // hover names a unit and has no aimed cell, so it gets nothing, as before.
    if (args.cell === undefined && isAreaRange(selectWeapon(attacker, intent.weapon, intent.profile).range ?? [])) {
      return { illegal: `an area attack is forecast from its aimed cell (${selected.name})` };
    }
  }
  const effects = { ...(args.overlay !== undefined ? { overlay: args.overlay } : {}) };
  // An area attack (or a multi-target weapon) forecasts as one caught target of its fan-out, with
  // the catch's size and the Blast's measuring center, as `resolveAreaAttack` resolves it.
  const area = intent.tech ? undefined : areaCatchOf({ state, intent: args.cell !== undefined ? { ...intent, target: { kind: "cell", cell: args.cell } } : intent, effects });
  if (area !== undefined && !area.targetIds.includes(target.id)) return { illegal: `${target.name} is not caught by the area` };
  return forecastLegalAttack({ state, attacker, target, intent, effects, ...(area !== undefined ? { area } : {}) });
}

/** One area or multi-target attack, forecast per caught target, in catch order. Empty when nothing is caught. */
export function forecastAreaAttack(state: EncounterState, args: Omit<ForecastAttackArgs, "targetId"> & { targetId?: string }): { targetId: string; forecast: AttackForecastResult }[] {
  const attacker = state.combatants[args.attackerId];
  if (attacker === undefined) return [];
  const aim: RequestAttack["target"] = args.cell !== undefined ? { kind: "cell", cell: args.cell } : args.targetId !== undefined ? { kind: "unit", id: args.targetId } : { kind: "unit", id: attacker.id };
  const intent: RequestAttack = { type: "request_attack", attacker: attacker.id, target: aim, accuracy: args.accuracy ?? 0, ...(args.weapon !== undefined ? { weapon: args.weapon } : {}), ...(args.profile !== undefined ? { profile: args.profile } : {}) };
  const area = areaCatchOf({ state, intent, effects: { ...(args.overlay !== undefined ? { overlay: args.overlay } : {}) } });
  return (area?.targetIds ?? []).map((targetId) => ({ targetId, forecast: forecastAttack(state, { ...args, targetId }) }));
}

function forecastLegalAttack(args: {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  effects: EffectsConfig;
  /** An area attack's catch: its size (the `targetCount` fact) and the Blast's measuring center. */
  area?: { targetIds: string[]; measureFrom?: Cell };
}): AttackForecast {
  const { state, attacker, target, intent, effects, area } = args;

  // S1, S2, S2.5, S3 — the same calls, in the same order, with the same arguments `resolveSingleTarget` passes.
  const overlay = attackOverlay(effects);
  // A forecast never halts: a `gm_ruling` reads the recorded answer or `false`.
  const rulings = recordedChoices(intent.choices);
  const facts = deriveAttackFacts({ state, attacker, intent, effects });
  const { weapon, isActionAttack } = facts;
  const cover = foldAttackCover({ state, attacker, target, intent, weapon, isActionAttack, ...(area?.measureFrom !== undefined ? { measureFrom: area.measureFrom } : {}), overlay });
  const foldArgs = {
    state,
    attacker,
    target,
    overlay,
    rulings,
    weapon,
    rangedAttack: facts.rangedAttack,
    techAttack: facts.techAttack,
    weaponType: facts.weaponType,
    mount: facts.mount,
    firstAttackThisTurn: facts.firstAttackThisTurn,
    attackVerb: facts.attackVerb,
    actionId: facts.actionId,
    cover: cover.degree,
    targetCount: area?.targetIds.length ?? 1,
    // The real resolve reads the ledger `fireAttackDeclared` filled; the forecast has only what was preselected.
    chosen: intent.choices ?? {},
  };
  // The same discovery-then-settle pair `resolveSingleTarget` runs: a recorded consume answer is
  // honored, and an unanswered one forecasts the default (consume, so Lock On's +1 counts).
  let mods = foldAttackModifiers(foldArgs);
  if (mods.consumeOnHit.length > 0) {
    const consumeChoice = settleConsumeChoice({ attacker, consumable: mods.consumeOnHit, choices: intent.choices, interactive: false });
    if (!isNeedsDecision(consumeChoice)) mods = foldAttackModifiers({ ...foldArgs, consumeChoice });
  }

  const costs = intent.tech ? [] : forecastCosts({ state, attacker, intent, overlay });
  // An attacker-side `ignoreCover` (Vanguard 2) drops the cover term, as the resolution does.
  const coverDegree = mods.ignoreCover ? "none" : cover.degree;
  const coverAcc = mods.ignoreCover ? 0 : cover.acc;
  const contributions = forecastContributions({ intent, attacker, target, weapon, coverDegree, coverAcc, modifiers: mods.accuracyContributions });
  // The dry run (F-TH1): the attack roll as proposed, through its replacements, drawn by nothing.
  // Its net accuracy and its advantage are what the resolution's roll draws with.
  const { rolled } = proposedAttackRoll({ attacker, target, intent, weapon, overlay, techAttack: facts.techAttack, attackerAccuracy: mods.attackerAccuracy, targetAccuracy: mods.targetAccuracy, coverAcc, attackRoll: mods.attackRoll });
  const net = rolled.spec.accuracy;
  const advantage = rolled.spec.keepHighest !== undefined;
  const bonus = toHitBonus(attacker, weapon);
  const defence = defenseFor({ target, tech: intent.tech === true, smart: weapon.smart ?? false, evasionCap: mods.evasionCap });

  const damage =
    intent.tech || isNoDamageMeleeAction(intent.weapon) || archaicCantHarm(weapon, target)
      ? null
      : forecastDamage({ state, attacker, target, intent, weapon: mods.armorPiercing && !weapon.ap ? { ...weapon, ap: true } : weapon, overlay, ignoreTargetArmor: mods.ignoreTargetArmor, doubleDamage: mods.doubleDamage, substituteStructure: mods.substituteStructureDamage, overrideDamageType: mods.overrideDamageType });

  return {
    attackerId: attacker.id,
    targetId: target.id,
    weaponId: weapon.id,
    techAttack: facts.techAttack,
    contributions,
    net,
    bonus,
    cover: coverDegree,
    defence: {
      ...defence,
      // The `to_hit` step records the cap whenever S3 produced one on a non-tech attack; so does this.
      ...(!intent.tech && mods.evasionCap !== undefined ? { evasionCap: mods.evasionCap } : {}),
    },
    damage,
    riders: {
      ...(mods.missChances[0] !== undefined ? { missChance: mods.missChances[0].chance } : {}),
      advantage,
      consumeOnHit: mods.consumeOnHit,
      ...(costs.length > 0 ? { costs } : {}),
    },
    warnings: mods.warnings,
    hitChance: toHitChance({ net, bonus, defence: defence.value, advantage, noCrits: attacker.base.noCrits === true }),
  };
}

/**
 * The charge costs the declared fire raises with the intent's preselected answers, read off the
 * declared phase's dry run ({@link dryRunAttackDeclared}, the probe `previewAttackDeclaredChoice`
 * reads its prompt from). An unanswered pick halts the dry run, and then nothing is forecast (the
 * view's `dependsOn` says what it waits on). Each cost names the replacement that would waive it on
 * a crit.
 */
function forecastCosts(args: { state: EncounterState; attacker: Combatant; intent: RequestAttack; overlay: LoadedOverlay | undefined }): AttackForecastCost[] {
  const { state, attacker, intent, overlay } = args;
  if (overlay === undefined) return [];
  const run = dryRunAttackDeclared({
    state,
    attackerId: attacker.id,
    intent: { ...(intent.weapon !== undefined ? { weapon: intent.weapon } : {}), ...(intent.profile !== undefined ? { profile: intent.profile } : {}), ...(intent.choices !== undefined ? { choices: intent.choices } : {}) },
    overlay,
  });
  if (run.halt !== undefined) return [];
  const out: AttackForecastCost[] = [];
  for (const event of run.events) {
    if (event.kind !== "expend_charges") continue;
    const poolName = attacker.base.equippedSystems?.find((s) => s.id === event.pool)?.name ?? event.pool;
    const waiver = waiverOnCrit(event, { state, overlay });
    out.push({ pool: event.pool, poolName, amount: event.amount, ...(waiver !== undefined ? { waivedOnCrit: waiver } : {}) });
  }
  return out;
}

/**
 * The itemized net, in the receipt's order (`narrate.ts`'s `accuracyProvenanceRow`): the declared
 * accuracy, then every recorded contribution, then cover. The recorded contributions are S3's list
 * plus the firing weapon's innate accuracy, appended exactly as `drawToHit` appends it.
 */
function forecastContributions(args: {
  intent: RequestAttack;
  attacker: Combatant;
  target: Combatant;
  weapon: WeaponProfile;
  coverDegree: "none" | "soft" | "hard";
  coverAcc: number;
  modifiers: { name: string; carrier: string; side: "attacker" | "target"; value: number }[];
}): AttackForecastContribution[] {
  const { intent, attacker, target, weapon, coverDegree, coverAcc, modifiers } = args;
  const out: AttackForecastContribution[] = [];
  if (intent.accuracy !== 0) out.push({ kind: "intent", name: "declared", carrier: attacker.id, side: "attacker", value: intent.accuracy });
  for (const m of modifiers) out.push({ kind: "modifier", ...m });
  const weaponAccuracy = weapon.accuracy ?? 0;
  if (weaponAccuracy !== 0) out.push({ kind: "weapon", name: weapon.id, carrier: attacker.id, side: "attacker", value: weaponAccuracy });
  if (coverDegree !== "none") out.push({ kind: "cover", name: `${coverDegree} cover`, carrier: target.id, side: "target", value: coverAcc });
  return out;
}

function forecastDamage(args: {
  state: EncounterState;
  attacker: Combatant;
  target: Combatant;
  intent: RequestAttack;
  weapon: WeaponProfile;
  overlay: LoadedOverlay | undefined;
  ignoreTargetArmor: boolean;
  doubleDamage: boolean;
  substituteStructure: boolean;
  overrideDamageType: DamageType | undefined;
}): AttackForecastDamage {
  const { state, attacker, target, intent, weapon, overlay, ignoreTargetArmor, doubleDamage, substituteStructure, overrideDamageType } = args;
  const armor = effectiveTargetArmor(target, ignoreTargetArmor);
  // S4 asked interactively: an unanswered variable type comes back as a prompt, so every option is listed.
  const settled = resolveVariableDamageType({ attacker, weapon, overrideDamageType, choices: intent.choices, interactive: true });
  const perType: AttackForecastDamageType[] = [];
  [weapon.damage, ...(weapon.riders ?? [])].forEach((entry, instance) => {
    const types =
      "kind" in settled
        ? [...new Set(VARIABLE_DAMAGE_TYPE_OPTIONS.map((opt) => instanceDamageType({ entry, overrideDamageType, resolvedVariableType: opt, dtype: opt })))]
        : [instanceDamageType({ entry, overrideDamageType, resolvedVariableType: settled.resolvedVariableType, dtype: settled.dtype })];
    for (const type of types) {
      // As `applyDamageInstance` folds it, minus the reaction half (Brace) the forecast cannot know.
      const resistant = !ignoreTargetArmor && foldedResistance({ state, targetId: target.id, attackerId: attacker.id, fromAttack: true, dtype: type, overlay });
      perType.push({
        instance,
        type,
        // The packet's mitigation (`mitigateDamageInstance`): its armor term does not depend on the
        // damage amount, so a zero probe reads the rating it applies. Pre-roll, the forecast shows
        // the full rating on every armorable type; the resolution spends it once across them (BC-071).
        armor: mitigateDamageInstance({ target, instance: { dtype: type, amount: 0 }, ap: weapon.ap, armor: weapon.ap ? 0 : armor, resistant, reduce: 0, immune: false }).witness.armor,
        resistant,
        immune: immuneToDamage({ state, targetId: target.id, dtype: type, overlay }),
        doubled: doubleDamage && isPhysical(type),
      });
    }
  });
  return { ap: weapon.ap, armor, armorIgnored: ignoreTargetArmor, perType, doubled: doubleDamage, substituteStructure, overshield: overshieldValue(target) };
}

/**
 * The exact probability of the to-hit roll `rollToHit` makes: d20 (the higher of two under
 * advantage) + `bonus` + the accuracy pool, where a net of N rolls |N| d6 and adds (or subtracts)
 * the single highest (RAW, "Accuracy and Difficulty"). A hit meets or beats the defence; a crit is a hit totalling
 * 20 or more, unless the attacker never crits.
 */
export function toHitChance(args: { net: number; bonus: number; defence: number; advantage: boolean; noCrits: boolean }): { hit: number; crit: number } {
  const { net, bonus, defence, advantage, noCrits } = args;
  const d20: [number, number][] = [];
  for (let k = 1; k <= 20; k++) d20.push([k, advantage ? (k * k - (k - 1) * (k - 1)) / 400 : 1 / 20]);
  const n = Math.abs(net);
  const pool: [number, number][] = n === 0 ? [[0, 1]] : [];
  for (let m = 1; n > 0 && m <= 6; m++) pool.push([net > 0 ? m : -m, (m ** n - (m - 1) ** n) / 6 ** n]);
  let hit = 0;
  let crit = 0;
  for (const [face, pf] of d20) {
    for (const [mod, pm] of pool) {
      const total = face + bonus + mod;
      if (total < defence) continue;
      hit += pf * pm;
      if (total >= 20 && !noCrits) crit += pf * pm;
    }
  }
  return { hit, crit };
}
